import React, { useRef, useEffect, useMemo, useState } from 'react';
import * as THREE from 'three';
import './ReachMaterial';
import { MathType, ThreeElements } from '@react-three/fiber';
import { isMesh, toVec3 } from '@slope/three';

const v1 = /*@__PURE__*/ new THREE.Vector2();
const v2 = /*@__PURE__*/ new THREE.Vector2();
const v3 = /*@__PURE__*/ new THREE.Vector2();
const v4 = /*@__PURE__*/ new THREE.Vector3();

export type ReachProps = ThreeElements['mesh'] & {
  mesh?: React.RefObject<THREE.Mesh>;
  center?: MathType<THREE.Vector3>;
  color?: MathType<THREE.Color>;
  radius?: number;
  lineThickness?: number;
  dashCount?: number;
  /**
   * Ratio of the dash length to the total length of the dash segment.
   * This is used to calculate the length of each dash segment based on the radius only if dashCount is not specified.
   * If not specified, it defaults to 0.3.
   */
  dashRatio?: number;
  /**
   * Length of each dash segment in world units.
   * This is used to calculate the number of dashes based on the radius only if dashCount is not specified.
   * If not specified, it defaults to 1.
   */
  dashLength?: number;
  opacity?: number;
  depthWrite?: boolean;
  polygonOffsetFactor?: number;
  debug?: boolean;
};

export const Reach: React.FC<ReachProps> = ({
  mesh,
  center: centerProp = [0, 0, 0],
  radius = 5,
  lineThickness = 0.1,
  dashCount: dashCountProp,
  dashRatio = 0.3,
  dashLength = 1,
  color = '#ffffff',
  opacity = 1,
  children,
  depthWrite,
  polygonOffsetFactor = -1,
  debug = false,
  ...restProps
}) => {
  const meshRef = useRef<THREE.Mesh>(null!);

  const [parent, setParent] = useState<THREE.Mesh | undefined>(undefined);

  const bbox = useMemo(() => {
    if (!parent) {
      return new THREE.Box3();
    }

    if (parent.geometry.boundingBox === null) {
      parent.updateWorldMatrix(true, false);
      parent.geometry.computeBoundingBox();
    }
    return parent.geometry.boundingBox!;
  }, [parent]);

  const shaderProps = useMemo(() => {
    if (!parent) {
      return {};
    }

    const boxMin = v1.clone().set(bbox.min.x, bbox.min.z);
    const boxMax = v2.clone().set(bbox.max.x, bbox.max.z);
    const center = toVec3(centerProp, v4);

    const sizeX = bbox.max.x - bbox.min.x;
    const sizeZ = bbox.max.z - bbox.min.z;
    const centerUV = v3
      .clone()
      .set((center.x - bbox.min.x) / sizeX, (center.z - bbox.min.z) / sizeZ);

    const radiusUV = new THREE.Vector2(radius / sizeX, radius / sizeZ);

    return {
      boxMin,
      boxMax,
      centerUV,
      radiusUV,
    };
  }, [centerProp, radius, bbox]);

  useEffect(() => {
    const parent = mesh?.current ?? meshRef.current?.parent;
    if (!isMesh(parent)) {
      throw new Error('Reach must have a Mesh as parent or specify its "mesh" prop');
    }
    setParent(parent);
  }, [mesh, mesh?.current ?? meshRef.current?.parent]);

  const dashCount = useMemo(() => {
    return dashCountProp ?? Math.max(3, Math.floor((2 * Math.PI * radius) / dashLength));
  }, [radius]);

  return (
    <mesh ref={meshRef} {...restProps} geometry={parent?.geometry}>
      <circleDashMaterial
        transparent
        depthTest={false}
        {...shaderProps}
        dashCount={dashCount}
        dashRatio={dashRatio}
        thickness={lineThickness / radius}
        color={color}
        opacity={opacity}
        polygonOffset
        polygonOffsetFactor={polygonOffsetFactor}
        depthWrite={depthWrite}
      />
      {children}
      {debug && <box3Helper args={[bbox, 'royalblue']} />}
    </mesh>
  );
};
